import { useMemo, useState, type ReactNode } from "react";
import { useSearchParams } from "react-router-dom";
import type { PlanningDTO, ShiftDTO, StaffMemberDTO } from "@easyshop/shared";
import { ABSENCE_TYPE_LABELS } from "@easyshop/shared";
import { Button, Card, Empty, ErrorState, Loading, Pill, TypeBadge, cx, inputClass } from "@/components/ui";
import { useSession } from "@/hooks/useSession";
import { api } from "@/lib/api";
import { useInvalidatePlanning, useMyPlanning, usePlanning } from "@/lib/queries";
import { ShiftDrawer, type ShiftDraft } from "@/components/planning/ShiftDrawer";
import { TeamDrawer } from "@/components/planning/TeamDrawer";
import { DayPromoChips, PromoStrip, promosOn } from "@/components/planning/PromoChips";
import { addDaysYmd, dayShort, hoursFmt, isToday, mondayYmd, weekLabel } from "@/components/planning/utils";

/**
 * Planning des équipes (PC) : une boutique à la fois, semaine par semaine.
 * Lignes = personnes, colonnes = jours ; au-dessus de chaque jour, les promos
 * du jour et la couverture (personnes planifiées / effectif minimum).
 */
export function PlanningPage() {
  const { me } = useSession();
  const [params, setParams] = useSearchParams();
  const boutiqueId = params.get("boutique") ?? me.boutiques[0]?.id ?? "";
  const from = params.get("semaine") ?? mondayYmd();
  const set = (k: string, v: string) => {
    const next = new URLSearchParams(params);
    next.set(k, v);
    setParams(next, { replace: true });
  };
  const mine = useMyPlanning();

  if (me.boutiques.length === 0) return <Empty>Aucune boutique dans votre périmètre.</Empty>;

  return (
    <div className="flex flex-col gap-[18px]">
      <WeekPlanning
        boutiqueId={boutiqueId}
        from={from}
        onWeek={(w) => set("semaine", w)}
        boutiqueSelect={
          me.boutiques.length > 1 && (
            <select className={cx(inputClass, "font-semibold")} style={{ width: 220 }} value={boutiqueId} onChange={(e) => set("boutique", e.target.value)}>
              {me.boutiques.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.city}
                </option>
              ))}
            </select>
          )
        }
      />
      {(mine.data?.shifts.length ?? 0) > 0 && (
        <Card pad="px-[22px] py-5" className="max-w-[520px]">
          <div className="mb-2 text-[13.5px] font-bold">Mes prochains créneaux</div>
          {mine.data!.shifts.slice(0, 6).map((s) => (
            <div key={s.id} className="flex justify-between border-t border-line-soft py-2 text-[12.5px] first:border-t-0">
              <span className="first-letter:uppercase">
                {dayShort(s.date)} · {s.city}
              </span>
              <span className="font-semibold">
                {s.start}–{s.end}
              </span>
            </div>
          ))}
        </Card>
      )}
    </div>
  );
}

/**
 * Semaine de planning d'une boutique : barre d'outils (semaine, équipe, copie,
 * créneau), promos à venir et grille. Partagée par la page Planning et
 * l'onglet Planning de la fiche boutique.
 */
export function WeekPlanning({
  boutiqueId,
  from,
  onWeek,
  boutiqueSelect,
}: {
  boutiqueId: string;
  from: string;
  onWeek: (monday: string) => void;
  boutiqueSelect?: ReactNode;
}) {
  const q = usePlanning(from, boutiqueId ? [boutiqueId] : []);
  const [draft, setDraft] = useState<ShiftDraft | null>(null);
  const [teamOpen, setTeamOpen] = useState(false);
  const [copyMsg, setCopyMsg] = useState<string | null>(null);
  const invalidate = useInvalidatePlanning();
  const d = q.data;
  const boutique = d?.boutiques.find((b) => b.id === boutiqueId) ?? null;

  const copyPrevious = async () => {
    setCopyMsg(null);
    try {
      const r = await api.post<{ created: number; skipped: number }>("/shifts/copy-week", {
        boutiqueId,
        fromWeek: addDaysYmd(from, -7),
        toWeek: from,
      });
      setCopyMsg(`${r.created} créneau(x) recopié(s)${r.skipped ? `, ${r.skipped} ignoré(s) (conflit ou absence)` : ""}.`);
      await invalidate();
    } catch (e) {
      setCopyMsg(e instanceof Error ? e.message : "Copie impossible");
    }
  };

  return (
    <div className="flex flex-col gap-[18px]">
      {/* Barre d'outils */}
      <div className="flex flex-wrap items-center gap-3">
        {boutiqueSelect}
        {boutiqueSelect && boutique && <TypeBadge type={boutique.type} />}
        <div className="flex items-center gap-1 rounded-full bg-white p-1 ring-1 ring-line">
          <button type="button" className="rounded-full px-3 py-[6px] text-[14px] hover:bg-page" onClick={() => onWeek(addDaysYmd(from, -7))} aria-label="Semaine précédente">
            ←
          </button>
          <span className="px-2 text-[13px] font-semibold">{weekLabel(from)}</span>
          <button type="button" className="rounded-full px-3 py-[6px] text-[14px] hover:bg-page" onClick={() => onWeek(addDaysYmd(from, 7))} aria-label="Semaine suivante">
            →
          </button>
        </div>
        {from !== mondayYmd() && (
          <button type="button" className="text-[12.5px] font-semibold text-accent" onClick={() => onWeek(mondayYmd())}>
            Cette semaine
          </button>
        )}
        <div className="flex-1" />
        {d?.canManage && (
          <>
            <Button variant="outline" onClick={() => setTeamOpen(true)}>
              Équipe
            </Button>
            <Button variant="outline" onClick={() => void copyPrevious()}>
              Copier la semaine précédente
            </Button>
            <Button onClick={() => setDraft({ boutiqueId, date: isInWeek(from) ? todayYmd() : from })}>+ Créneau</Button>
          </>
        )}
      </div>
      {copyMsg && <div className="rounded-card-sm bg-accent-soft px-4 py-2 text-[12.5px] font-semibold text-[oklch(0.3_0.03_152)]">{copyMsg}</div>}

      {q.isLoading && <Loading />}
      {q.error && <ErrorState error={q.error} />}
      {d && boutique && <PromoStrip promos={d.upcomingPromos} title={`Prochaines promos — ${boutique.city}`} />}
      {d && boutique && <WeekGrid data={d} boutiqueId={boutiqueId} onCell={setDraft} />}

      {d && (
        <>
          <ShiftDrawer draft={draft} staff={d.staff} onClose={() => setDraft(null)} />
          {boutique && (
            <TeamDrawer
              open={teamOpen}
              onClose={() => setTeamOpen(false)}
              boutiqueId={boutiqueId}
              city={boutique.city}
              staff={d.staff}
              absences={d.absences}
            />
          )}
        </>
      )}
    </div>
  );
}

const todayYmd = () => addDaysYmd(mondayYmd(), (new Date().getDay() + 6) % 7);
const isInWeek = (from: string) => from === mondayYmd();

function WeekGrid({ data, boutiqueId, onCell }: { data: PlanningDTO; boutiqueId: string; onCell: (d: ShiftDraft) => void }) {
  const staffRows = useMemo(() => {
    const inShifts = new Set(data.shifts.filter((s) => s.boutiqueId === boutiqueId).map((s) => s.staffMemberId));
    return data.staff.filter((s) => (s.boutiqueId === boutiqueId && s.active) || inShifts.has(s.id));
  }, [data, boutiqueId]);
  const cols = `minmax(170px,1.3fr) repeat(${data.days.length}, minmax(96px,1fr))`;

  if (staffRows.length === 0) {
    return (
      <Card className="text-center">
        <div className="text-[14px] font-bold">Aucune équipe pour cette boutique</div>
        <div className="mt-1 text-[12.5px] text-muted">
          {data.canManage ? "Ajoutez les personnes de la boutique avec le bouton « Équipe »." : "Le planning n'a pas encore été préparé."}
        </div>
      </Card>
    );
  }

  return (
    <div className="overflow-x-auto rounded-card border border-line bg-white">
      <div style={{ minWidth: 170 + data.days.length * 100 }}>
        {/* En-têtes : jour, promos, couverture */}
        <div className="grid border-b border-line-soft" style={{ gridTemplateColumns: cols }}>
          <div className="px-4 py-3 text-[10.5px] font-bold text-muted-2">ÉQUIPE</div>
          {data.days.map((day) => {
            const cov = data.coverage.find((c) => c.boutiqueId === boutiqueId && c.date === day);
            return (
              <div key={day} className={cx("border-l border-line-row px-2 py-3", isToday(day) && "bg-accent-row")}>
                <div className={cx("text-[12px] font-bold capitalize", isToday(day) && "text-accent")}>{dayShort(day)}</div>
                {cov && (cov.staff > 0 || cov.understaffed) && (
                  <Pill
                    className={cx("mt-1", cov.understaffed ? "bg-danger-bg text-danger" : "bg-positive-bg text-positive")}
                    style={{ fontSize: 10 }}
                  >
                    {cov.staff} / {cov.minStaff} pers.
                  </Pill>
                )}
                <DayPromoChips promos={promosOn(data.promos, day, boutiqueId)} />
              </div>
            );
          })}
        </div>

        {/* Une ligne par personne */}
        {staffRows.map((s) => (
          <StaffRow key={s.id} s={s} data={data} boutiqueId={boutiqueId} cols={cols} onCell={onCell} />
        ))}

        {/* Totaux du jour */}
        <div className="grid bg-page" style={{ gridTemplateColumns: cols }}>
          <div className="px-4 py-3 text-[11.5px] font-bold text-muted">Heures planifiées</div>
          {data.days.map((day) => {
            const cov = data.coverage.find((c) => c.boutiqueId === boutiqueId && c.date === day);
            return (
              <div key={day} className="border-l border-line-row px-2 py-3 text-[12px] font-bold tabular">
                {cov && cov.hours > 0 ? hoursFmt(cov.hours) : "—"}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function StaffRow({
  s,
  data,
  boutiqueId,
  cols,
  onCell,
}: {
  s: StaffMemberDTO;
  data: PlanningDTO;
  boutiqueId: string;
  cols: string;
  onCell: (d: ShiftDraft) => void;
}) {
  const shifts = data.shifts.filter((x) => x.staffMemberId === s.id);
  const week = shifts.reduce((sum, x) => sum + x.hours, 0);
  const over = s.contractHours != null && week > s.contractHours;
  const guest = s.boutiqueId !== boutiqueId;
  return (
    <div className="grid border-b border-line-row" style={{ gridTemplateColumns: cols }}>
      <div className="flex items-center gap-[10px] px-4 py-3">
        <span className="h-[10px] w-[10px] flex-shrink-0 rounded-full" style={{ background: s.color ?? "var(--accent)" }} />
        <div className="min-w-0">
          <div className="truncate text-[12.5px] font-semibold">{s.name}</div>
          <div className="truncate text-[11px] text-muted-2">
            {guest ? "Renfort · " : s.jobTitle ? `${s.jobTitle} · ` : ""}
            <span className={cx(over && "font-bold text-danger")}>
              {hoursFmt(week)}
              {s.contractHours != null ? ` / ${s.contractHours} h` : ""}
            </span>
          </div>
        </div>
      </div>
      {data.days.map((day) => {
        const dayShifts = shifts.filter((x) => x.date === day);
        const abs = data.absences.find((a) => a.staffMemberId === s.id && a.startDate <= day && a.endDate >= day);
        return (
          <div
            key={day}
            className={cx(
              "group flex min-h-[58px] flex-col gap-1 border-l border-line-row p-[6px]",
              isToday(day) && "bg-[oklch(0.985_0.012_152)]"
            )}
          >
            {abs && <div className="rounded-[8px] bg-warn-bg px-2 py-[5px] text-[11px] font-bold text-warn">{ABSENCE_TYPE_LABELS[abs.type]}</div>}
            {dayShifts.map((x) => (
              <ShiftChip
                key={x.id}
                shift={x}
                color={s.color}
                other={x.boutiqueId !== boutiqueId}
                onClick={data.canManage ? () => onCell({ shift: x, boutiqueId: x.boutiqueId, date: day }) : undefined}
              />
            ))}
            {!abs && data.canManage && s.active && (
              <button
                type="button"
                onClick={() => onCell({ boutiqueId, staffMemberId: s.id, date: day })}
                className="rounded-[8px] border border-dashed border-line py-[3px] text-[12px] text-faint opacity-0 transition-opacity hover:text-accent group-hover:opacity-100 focus:opacity-100"
                aria-label={`Ajouter un créneau pour ${s.name}`}
              >
                +
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}

function ShiftChip({ shift, color, other, onClick }: { shift: ShiftDTO; color: string | null; other: boolean; onClick?: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!onClick}
      title={shift.note ?? undefined}
      className="rounded-[8px] px-2 py-[5px] text-left text-[11.5px] font-bold text-white disabled:cursor-default"
      style={{ background: other ? "oklch(0.75 0.01 150)" : (color ?? "var(--accent)") }}
    >
      {shift.start}–{shift.end}
      {shift.breakMinutes ? <span className="block text-[10px] font-semibold opacity-80">pause {shift.breakMinutes} min</span> : null}
      {shift.note ? <span className="block truncate text-[10px] font-semibold opacity-80">{shift.note}</span> : null}
    </button>
  );
}

